import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Star, MapPin, Briefcase, Award, Loader2, UserCheck, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AgentMatcher = () => {
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [listingType, setListingType] = useState('sale');
  const [budget, setBudget] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const findAgents = async () => {
    if (!query && !city) {
      toast.error('Please enter a search query or city');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/agent-matching`, {
        query, city, property_type: propertyType, listing_type: listingType, budget
      });
      setResults(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="agent-matcher">
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-[#E07A3A]" />
          Find Your Perfect Agent
        </h3>
        <p className="text-sm text-slate-400 mb-4">Our AI matches you with the best agent based on your requirements, their experience, and track record.</p>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase block mb-1">What are you looking for?</label>
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 3 bed house with garden"
              className="h-11 bg-[#F8FAFC]" data-testid="agent-match-query" />
          </div>
          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase block mb-1">City / Area</label>
            <Input value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. London, Manchester"
              className="h-11 bg-[#F8FAFC]" data-testid="agent-match-city" />
          </div>
          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase block mb-1">Property Type</label>
            <Select value={propertyType} onValueChange={setPropertyType}>
              <SelectTrigger className="h-11 bg-[#F8FAFC]" data-testid="agent-match-type">
                <SelectValue placeholder="Any type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="house">House</SelectItem>
                <SelectItem value="apartment">Apartment / Flat</SelectItem>
                <SelectItem value="villa">Villa</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase block mb-1">Budget</label>
            <Input value={budget} onChange={(e) => setBudget(e.target.value)}
              placeholder="e.g. 500000"
              className="h-11 bg-[#F8FAFC]" data-testid="agent-match-budget" />
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <label className="text-xs text-slate-500 tracking-wider uppercase">Looking to:</label>
          <div className="flex gap-2">
            <button onClick={() => setListingType('sale')}
              className={`px-4 py-1.5 text-xs tracking-wider font-semibold ${listingType === 'sale' ? 'bg-[#E07A3A] text-white' : 'bg-slate-100 text-slate-500'}`}
              data-testid="match-type-sale">BUY</button>
            <button onClick={() => setListingType('rent')}
              className={`px-4 py-1.5 text-xs tracking-wider font-semibold ${listingType === 'rent' ? 'bg-[#E07A3A] text-white' : 'bg-slate-100 text-slate-500'}`}
              data-testid="match-type-rent">RENT</button>
          </div>
        </div>

        <Button onClick={findAgents} disabled={loading}
          className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm px-8 py-5 font-semibold w-full sm:w-auto"
          data-testid="find-agents-btn">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Matching...</> : <><Search className="mr-2 h-4 w-4" />Find Agents</>}
        </Button>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-4" data-testid="agent-match-results">
          {results.ai_summary && (
            <div className="luxury-card p-4 bg-[#FEF3EC] border-[#E07A3A]/20">
              <p className="text-sm text-[#1A1A1A] font-medium flex items-start gap-2">
                <Star className="h-4 w-4 text-[#E07A3A] flex-shrink-0 mt-0.5" />
                {results.ai_summary}
              </p>
            </div>
          )}

          {results.matches?.length > 0 ? (
            results.matches.map((agent, idx) => (
              <div key={agent.id} className={`luxury-card p-5 ${idx === 0 ? 'border-l-4 border-l-[#E07A3A]' : ''}`}
                data-testid={`agent-match-${agent.id}`}>
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#FEF3EC] flex items-center justify-center flex-shrink-0">
                        <span className="text-[#E07A3A] font-bold text-lg">{agent.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0F172A] text-sm">{agent.name}</h4>
                        <p className="text-xs text-slate-400">{agent.company}</p>
                      </div>
                      <div className={`px-3 py-1 text-sm font-bold ${
                        agent.match_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                        agent.match_score >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-500'
                      }`} data-testid={`match-score-${agent.id}`}>
                        {agent.match_score}%
                      </div>
                    </div>

                    <p className="text-xs text-slate-500 mb-3">{agent.match_reason}</p>

                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      {agent.years_experience && (
                        <span className="flex items-center gap-1"><Award className="h-3 w-3 text-[#E07A3A]" />{agent.years_experience} years</span>
                      )}
                      {agent.areas_covered?.length > 0 && (
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-[#E07A3A]" />{agent.areas_covered.slice(0, 3).join(', ')}</span>
                      )}
                      {agent.specializations?.length > 0 && (
                        <span className="flex items-center gap-1"><Briefcase className="h-3 w-3 text-[#E07A3A]" />{agent.specializations.slice(0, 2).join(', ')}</span>
                      )}
                      <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3 text-[#E07A3A]" />{agent.completed_deals} deals closed</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="luxury-card p-8 text-center text-slate-400">
              No agents found matching your criteria. Try a different search.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentMatcher;
